/**
 * 🔈 Audio channel claim/release singleton.
 *
 * Problem: iOS WebKit (Chrome on iOS uses WebKit) has a hard
 * one-channel-at-a-time limit for HTML5 Audio. While the background
 * music is playing, .play() calls on a brand-new Audio for an air-raid
 * siren / bomb blast / nuke explosion silently fail with NotAllowedError.
 *
 * Pausing the music *via React state* doesn't work because of effect
 * ordering: child SFX components mount + try to play their sound BEFORE
 * the parent music hook gets a chance to react to the new state and pause.
 *
 * This module gives SFX components a way to imperatively pause the music
 * *synchronously* the instant they need the audio channel, and release
 * it back when their animation finishes.
 *
 * Usage:
 *   - useBackgroundMusic hook calls `registerBackgroundMusic(audio)` every
 *     time it instantiates a new track.
 *   - Cinematic / SFX components call `claimAudioChannel()` at the very
 *     top of their mount effect, BEFORE creating their own Audio.
 *   - On cleanup they call `releaseAudioChannel()`.
 */

let musicRef = null;
let claimCount = 0;

/**
 * Called by useBackgroundMusic each time a new background track is loaded.
 * If a scene is currently active, the freshly registered track is paused
 * immediately so it doesn't steal the audio channel.
 */
export const registerBackgroundMusic = (audio) => {
  musicRef = audio;
  if (claimCount > 0 && audio && !audio.paused) {
    audio.pause();
  }
};

/**
 * Pause the background music to free the audio channel for an SFX-heavy
 * scene. Safe to call multiple times — each call increments a counter and
 * the music only resumes when all claims are released.
 *
 * As a safety net we also pause every `<audio>` element in the DOM and any
 * tracked Audio instance we know about. This covers cases where the very
 * first action fires before the background-music hook has finished mounting
 * (iOS first-gesture autoplay race) — without this, the music would
 * silently hog the audio channel and the first siren/blackmail/etc. would
 * play with no sound.
 */
export const claimAudioChannel = () => {
  claimCount += 1;
  if (musicRef && !musicRef.paused) {
    try {
      musicRef.pause();
    } catch (_) { /* ignore */ }
  }
  // Aggressive safety net for iOS WebKit: pause ANY <audio> element that
  // happens to be playing background music. SFX components create their
  // own Audio instances after this returns, so we won't accidentally mute
  // sounds the caller is about to play.
  if (typeof document !== 'undefined') {
    try {
      document.querySelectorAll('audio').forEach((el) => {
        if (!el.paused) {
          el.pause();
        }
      });
    } catch (_) { /* ignore */ }
  }
};

/**
 * Release a previously claimed channel. Resumes background music when the
 * last claim is released.
 */
export const releaseAudioChannel = () => {
  claimCount = Math.max(0, claimCount - 1);
  if (claimCount === 0 && musicRef && musicRef.paused) {
    try {
      const p = musicRef.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* autoplay may be blocked; ignore */ });
      }
    } catch (_) { /* ignore */ }
  }
};

/**
 * Force-release all claims (used when game state resets, e.g., turn end).
 */
export const resetAudioClaims = () => {
  claimCount = 0;
  if (musicRef && musicRef.paused) {
    try {
      const p = musicRef.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* ignore */ });
      }
    } catch (_) { /* ignore */ }
  }
};
